/**
 * component-product-buy-box.js
 * Logique du bloc d'achat — variant picker + AJAX add-to-cart + qty stepper
 * Lot 3 Batch A — EDJ Shopify theme
 *
 * Ref : page-produit-a-desktop.jsx 159-207 + page-produit-a-mobile.jsx 157-210
 *
 * Comportements :
 *   - Click swatch / pill → met à jour la sélection des options
 *   - Calcule le variant correspondant via window.__edjProduct[handle] (si fourni)
 *     ou retombe sur la sélection des inputs radio standard
 *   - Met à jour l'URL (variant=ID) via History API
 *   - Met à jour le total CTA (price × qty) à la volée
 *   - Qty stepper +/- (min 1)
 *   - Submit form → /cart/add.js AJAX, dispatch 'cart:added'
 *   - Animation "✓ Ajouté au panier" 2s
 *
 * Note : aucune écriture innerHTML — toute la mise à jour de label se fait
 * via textContent et nodes DOM créés sans interpolation de chaîne.
 */
(function () {
  'use strict';

  const FORMAT_FALLBACK = '€{{amount_no_decimals_with_comma_separator}}';

  function moneyFormat(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const fmt = (window.theme && window.theme.moneyFormat) || FORMAT_FALLBACK;
      try { return window.Shopify.formatMoney(cents, fmt); } catch (e) {}
    }
    const eur = (cents / 100);
    return eur.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' €';
  }

  function buildAddLabel(totalCents) {
    // "Ajouter au panier — <span data-edj-atc-total>NN €</span>"
    const frag = document.createDocumentFragment();
    frag.appendChild(document.createTextNode('Ajouter au panier — '));
    const span = document.createElement('span');
    span.setAttribute('data-edj-atc-total', '');
    span.textContent = moneyFormat(totalCents);
    frag.appendChild(span);
    return frag;
  }

  class EdjProductBuyBox {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-edj-product-form]');
      if (!this.form) return;

      this.qtyInput = root.querySelector('[data-edj-qty-input]');
      this.qtyMinus = root.querySelector('[data-edj-qty-minus]');
      this.qtyPlus = root.querySelector('[data-edj-qty-plus]');
      this.atc = root.querySelector('[data-edj-atc]');
      this.atcLabel = root.querySelector('[data-edj-atc-label]');
      this.priceEl = root.querySelector('[data-edj-price]');
      this.compareEl = root.querySelector('[data-edj-compare]');
      this.savingEl = root.querySelector('[data-edj-saving]');
      this.klarnaEl = root.querySelector('[data-edj-klarna]');
      this.klarnaLineEl = root.querySelector('[data-edj-klarna-line]');
      this.variantInput = root.querySelector('[data-edj-variant-id]');
      this.optionInputs = Array.from(root.querySelectorAll('[data-edj-option-input]'));
      this.optionValues = Array.from(root.querySelectorAll('[data-edj-option-value]'));

      // Hooks externes (gallery badge)
      this.galleryBadge = document.querySelector('[data-edj-gallery-badge]');
      this.galleryBadgePct = document.querySelector('[data-edj-gallery-badge-pct]');

      this.productData = this._getProductData();
      this.currentVariant = this._findVariantFromInputs();

      this._bind();
      this._syncSelectionUI();
      this._updateAtcTotal();

      // Items 3+4+12 : forcer une synchro initiale du DOM promo avec la variante
      // sélectionnée (utile si on arrive via ?variant=ID-promo ou si la variante
      // par défaut est en promo). Sans ça, le DOM Liquid initial peut être
      // désynchronisé du JS handler.
      if (this.productData && this.currentVariant) {
        this._syncPromoUI(this.currentVariant);
      }
    }

    _getProductData() {
      const handle = this.root.dataset.productHandle;
      if (handle && window.__edjProduct && window.__edjProduct[handle]) {
        return window.__edjProduct[handle];
      }
      return null;
    }

    _bind() {
      this.optionInputs.forEach(input => {
        input.addEventListener('change', () => this._onOptionChange());
      });

      this.root.querySelectorAll('[data-edj-swatch], [data-edj-pill]').forEach(label => {
        label.addEventListener('click', () => {
          requestAnimationFrame(() => this._syncSelectionUI());
        });
      });

      this.qtyMinus?.addEventListener('click', () => this._setQty(this._getQty() - 1));
      this.qtyPlus?.addEventListener('click', () => this._setQty(this._getQty() + 1));
      this.qtyInput?.addEventListener('change', () => this._setQty(this._getQty()));
      this.qtyInput?.addEventListener('input', () => this._updateAtcTotal());

      this.form.addEventListener('submit', e => this._onSubmit(e));
    }

    _getQty() {
      return Math.max(1, parseInt(this.qtyInput?.value || '1', 10) || 1);
    }

    _setQty(n) {
      const q = Math.max(1, n);
      if (this.qtyInput) this.qtyInput.value = String(q);
      this._updateAtcTotal();
    }

    _selectedOptions() {
      const map = {};
      this.optionInputs.forEach(input => {
        if (!input.checked) return;
        const idx = parseInt(input.dataset.optionIndex, 10);
        if (Number.isFinite(idx)) map[idx] = input.value;
      });
      return map;
    }

    _findVariantFromInputs() {
      if (!this.productData || !Array.isArray(this.productData.variants)) return null;
      const sel = this._selectedOptions();
      const optCount = this.productData.options ? this.productData.options.length : 0;
      return this.productData.variants.find(v => {
        for (let i = 0; i < optCount; i++) {
          if (sel[i] && v.options[i] !== sel[i]) return false;
        }
        return true;
      }) || null;
    }

    _syncSelectionUI() {
      this.root.querySelectorAll('[data-edj-swatch], [data-edj-pill]').forEach(label => {
        const input = label.querySelector('input[type="radio"]');
        if (!input) return;
        label.classList.toggle('is-active', input.checked);
      });

      const sel = this._selectedOptions();
      this.optionValues.forEach(el => {
        const idx = parseInt(el.dataset.edjOptionValue, 10);
        if (Number.isFinite(idx) && sel[idx]) el.textContent = sel[idx];
      });
    }

    _syncPromoUI(variant) {
      if (!variant) return;
      if (this.variantInput) this.variantInput.value = String(variant.id);

      if (this.priceEl && typeof variant.price === 'number') {
        this.priceEl.textContent = moneyFormat(variant.price);
      }
      if (this.klarnaEl && typeof variant.price === 'number') {
        this.klarnaEl.textContent = moneyFormat(Math.round(variant.price / 3));
      }
      // Item 5 : phrase Klarna configurable. Si la ligne expose son template,
      // on recalcule avec __N__ et __PRICE__ par échéance.
      if (this.klarnaLineEl && typeof variant.price === 'number') {
        const tpl = this.klarnaLineEl.dataset.edjKlarnaTemplate
          || 'ou __N__× __PRICE__ sans frais avec Klarna · TVA incluse';
        const nRaw = parseInt(this.klarnaLineEl.dataset.edjKlarnaInstallments, 10);
        const n = Number.isFinite(nRaw) && nRaw >= 2 ? nRaw : 3;
        const each = Math.round(variant.price / n);
        const txt = tpl.replace(/__N__/g, String(n)).replace(/__PRICE__/g, moneyFormat(each));
        this.klarnaLineEl.textContent = txt;
      }

      // Promo par variante : update compare, saving, gallery badge
      const onSale = typeof variant.compare_at_price === 'number'
        && variant.compare_at_price > variant.price;

      if (this.compareEl) {
        if (onSale) {
          this.compareEl.textContent = moneyFormat(variant.compare_at_price);
          this.compareEl.hidden = false;
          this.compareEl.removeAttribute('hidden');
        } else {
          this.compareEl.hidden = true;
          this.compareEl.setAttribute('hidden', '');
        }
      }
      if (this.savingEl) {
        if (onSale) {
          const saved = variant.compare_at_price - variant.price;
          this.savingEl.textContent = '−' + moneyFormat(saved);
          this.savingEl.hidden = false;
          this.savingEl.removeAttribute('hidden');
        } else {
          this.savingEl.hidden = true;
          this.savingEl.setAttribute('hidden', '');
        }
      }
      if (this.galleryBadge) {
        if (onSale) {
          const pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          if (this.galleryBadgePct) this.galleryBadgePct.textContent = String(pct);
          this.galleryBadge.setAttribute('aria-label', 'Promotion ' + pct + ' pourcent');
          this.galleryBadge.hidden = false;
          this.galleryBadge.removeAttribute('hidden');
        } else {
          this.galleryBadge.hidden = true;
          this.galleryBadge.setAttribute('hidden', '');
        }
      }

      if (this.atc) {
        this.atc.disabled = !variant.available;
        if (this.atcLabel) {
          this.atcLabel.textContent = '';
          if (variant.available) {
            this.atcLabel.appendChild(buildAddLabel(variant.price * this._getQty()));
          } else {
            this.atcLabel.textContent = 'Épuisé';
          }
        }
      }
    }

    _onOptionChange() {
      this._syncSelectionUI();
      const variant = this._findVariantFromInputs();
      this.currentVariant = variant;

      if (variant) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          window.history.replaceState({}, '', url.toString());
        } catch (e) {}

        this._syncPromoUI(variant);
      }

      this._updateAtcTotal();

      if (window.EDJ && window.EDJ.events) {
        window.EDJ.events.emit('variant:change', {
          productHandle: this.root.dataset.productHandle,
          variantId: variant ? variant.id : null,
          variant,
        });
      }
    }

    _updateAtcTotal() {
      if (!this.atcLabel || !this.atc || this.atc.disabled) return;
      const totalSpan = this.atcLabel.querySelector('[data-edj-atc-total]');
      const v = this.currentVariant;
      const priceCents = v && typeof v.price === 'number'
        ? v.price
        : this._initialPriceCents();
      const total = priceCents * this._getQty();
      if (totalSpan) {
        totalSpan.textContent = moneyFormat(total);
      } else {
        // No total span (e.g. label was set to a plain string); restore.
        this.atcLabel.textContent = '';
        this.atcLabel.appendChild(buildAddLabel(total));
      }
    }

    _initialPriceCents() {
      if (this._cachedPriceCents != null) return this._cachedPriceCents;
      const txt = this.priceEl?.textContent || '';
      const num = parseFloat(txt.replace(/[^\d,.\-]/g, '').replace(',', '.'));
      this._cachedPriceCents = Number.isFinite(num) ? Math.round(num * 100) : 0;
      return this._cachedPriceCents;
    }

    _setLabelText(text) {
      if (!this.atcLabel) return;
      this.atcLabel.textContent = text;
    }

    async _onSubmit(e) {
      e.preventDefault();
      if (!this.atc || this.atc.disabled) return;

      const fd = new FormData(this.form);

      this.atc.disabled = true;
      this._setLabelText('Ajout en cours…');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data && data.description ? data.description : 'Erreur ajout au panier');
        }

        this.atc.classList.add('is-added');
        this._setLabelText('✓ Ajouté au panier');

        if (window.EDJ && window.EDJ.events) {
          window.EDJ.events.emit('cart:added', {
            item: data,
            productHandle: this.root.dataset.productHandle,
          });
        }
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data, bubbles: true }));

        setTimeout(() => {
          this.atc.classList.remove('is-added');
          this.atc.disabled = false;
          this._restoreAddLabel();
        }, 2000);
      } catch (err) {
        console.error('[EDJ] add to cart error:', err);
        this.atc.disabled = false;
        this._setLabelText('Erreur — réessayer');
        setTimeout(() => this._restoreAddLabel(), 2200);
      }
    }

    _restoreAddLabel() {
      if (!this.atcLabel) return;
      const v = this.currentVariant;
      const priceCents = v && typeof v.price === 'number' ? v.price : this._initialPriceCents();
      this.atcLabel.textContent = '';
      this.atcLabel.appendChild(buildAddLabel(priceCents * this._getQty()));
    }
  }

  function init() {
    document.querySelectorAll('[data-edj-buy-box]').forEach(el => {
      if (el.__edjBuyBoxInit) return;
      el.__edjBuyBoxInit = true;
      new EdjProductBuyBox(el);
    });
  }

  /* ── Itération 2026-05-04 Batch 5 — Item mobile #3
     Réordonnancement mobile : la section product-description doit s'afficher
     entre la trust grid (4 blocs, fin du buy-box) et les accordions
     (Composition & matière, etc., dans le buy-box).
     Solution : déplacer .product-description (Shopify section wrapper inclus)
     dans .edj-product-buy-box JUSTE AVANT .edj-product-buy-box__accordions
     uniquement <768px. Si l'utilisateur passe en desktop, on remet en place. */
  function reorderMobileDescription() {
    const descSection = document.querySelector('.product-description');
    if (!descSection) return;
    const sectionWrapper = descSection.closest('[id^="shopify-section-"]') || descSection;
    const accordions = document.querySelector('.edj-product-buy-box__accordions');
    if (!accordions) return;

    // Crée un placeholder invisible à la position d'origine pour pouvoir
    // restaurer si l'utilisateur redimensionne en desktop.
    if (!sectionWrapper.__edjPlaceholder) {
      const placeholder = document.createComment('edj-desc-original-position');
      sectionWrapper.parentNode.insertBefore(placeholder, sectionWrapper);
      sectionWrapper.__edjPlaceholder = placeholder;
    }

    const mq = window.matchMedia('(max-width: 767.98px)');

    function applyReorder() {
      if (mq.matches) {
        // Mobile : déplacer juste avant les accordions du buy-box
        if (sectionWrapper.nextElementSibling !== accordions
            || !accordions.parentNode.contains(sectionWrapper)) {
          accordions.parentNode.insertBefore(sectionWrapper, accordions);
        }
      } else {
        // Desktop : restaurer à la position d'origine
        const placeholder = sectionWrapper.__edjPlaceholder;
        if (placeholder && placeholder.parentNode
            && placeholder.parentNode !== sectionWrapper.parentNode) {
          placeholder.parentNode.insertBefore(sectionWrapper, placeholder);
        }
      }
    }

    applyReorder();
    if (mq.addEventListener) {
      mq.addEventListener('change', applyReorder);
    } else if (mq.addListener) {
      mq.addListener(applyReorder);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      reorderMobileDescription();
    });
  } else {
    init();
    reorderMobileDescription();
  }
})();
